import React, { useState, useEffect } from 'react';
import { Filter } from 'lucide-react';
import { getRealizations } from '../services/apiService';

const Realizations = () => {
  const [filter, setFilter] = useState('all');
  const [realizations, setRealizations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRealizations = async () => {
      try {
        setLoading(true);
        const data = await getRealizations(filter === 'all' ? null : filter);
        setRealizations(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching realizations:', error);
        setLoading(false);
      }
    };

    fetchRealizations();
  }, [filter]);

  const categories = ['all', 'CO2', 'Fibre', 'Mixte'];

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#5C3A21' }}></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const filteredRealizations = realizations;

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Nos Réalisations
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Découvrez des exemples concrets de projets réalisés avec nos machines laser.<br />
            Artisans, industriels, créateurs : ils ont tous réussi avec AKM Laser.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white sticky top-20 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5" style={{ color: '#5C3A21' }} />
              <span className="font-semibold text-gray-900">Filtrer par type :</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-300 ${
                    filter === category
                      ? 'text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={filter === category ? { backgroundColor: '#5C3A21' } : {}}
                >
                  {category === 'all' ? 'Tous' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Realizations Grid */}
      <section className="py-16" style={{ backgroundColor: '#F5EBDD' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredRealizations.map((realization) => (
              <div
                key={realization.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={realization.image}
                    alt={realization.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#5C3A21' }}>
                      {realization.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#5C3A21' }}>
                    {realization.title}
                  </h3>
                  {realization.realizationType && (
                    <p className="text-sm text-gray-600 mb-4">
                      Type :{' '}
                      <span className="font-semibold">
                        {realization.realizationType === 'gravure' && 'Gravure'}
                        {realization.realizationType === 'decoupe' && 'Découpe'}
                        {realization.realizationType === 'gravure_decoupe' && 'Gravure & Découpe'}
                      </span>
                    </p>
                  )}
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {realization.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredRealizations.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">Aucune réalisation trouvée pour ce filtre.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Votre projet sera le prochain ?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Partagez-nous votre idée. Nous vous conseillons la machine adaptée<br />
            et vous accompagnons jusqu'à la réussite de votre projet.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-lg"
            style={{ color: '#5C3A21' }}
          >
            Discutons de votre projet
          </a>
        </div>
      </section>
    </div>
  );
};

export default Realizations;
